import type { Residence, ResidenceInput } from '../types/person.js';

export interface ParsedResidenceRow {
  /** 1-based row number in the pasted text, for error messages. */
  row: number;
  person: string;
  residence: ResidenceInput;
}

export interface ResidenceSheetError {
  row: number;
  message: string;
}

export interface ParsedResidenceSheet {
  rows: ParsedResidenceRow[];
  errors: ResidenceSheetError[];
}

type Column =
  | 'person' | 'from' | 'to' | 'unit' | 'streetNumber' | 'street' | 'city' | 'region' | 'postalCode' | 'country';

/** Column order of the address-history sheet when no header row is pasted. */
const DEFAULT_COLUMNS: Column[] = [
  'person', 'from', 'to', 'unit', 'streetNumber', 'street', 'city', 'region', 'postalCode', 'country',
];

const HEADER_PATTERNS: Array<[Column, RegExp]> = [
  ['person', /person|name|who/],
  ['from', /^from|start|move.?in/],
  ['to', /^to\b|^to\s*\(|end|move.?out|until/],
  ['unit', /unit|apartment|apt|suite/],
  ['streetNumber', /house|street\s*#|street\s*no|number/],
  ['street', /street|address/],
  ['city', /city|town/],
  ['region', /province|state|region/],
  ['postalCode', /postal|zip|pin/],
  ['country', /country/],
];

const PRESENT = /^(present|current|now|-)?$/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parses rows copied from a spreadsheet (tab-separated, as Google Sheets and Excel put on the
 * clipboard). Cells containing newlines arrive quoted, e.g. a two-line street address.
 * Accepts an optional header row; without one, columns follow the address-history sheet:
 * Person, From, To, Unit, House #, Street, City, Province/State, Postal code, Country.
 */
export function parseResidenceSheet(text: string): ParsedResidenceSheet {
  const table = parseTsv(text);
  const rows: ParsedResidenceRow[] = [];
  const errors: ResidenceSheetError[] = [];
  if (table.length === 0) return { rows, errors };

  let columns = DEFAULT_COLUMNS;
  let start = 0;
  const headerColumns = detectHeader(table[0]);
  if (headerColumns) {
    columns = headerColumns;
    start = 1;
  }

  for (let i = start; i < table.length; i++) {
    const rowNumber = i + 1;
    const cells: Partial<Record<Column, string>> = {};
    columns.forEach((col, idx) => {
      cells[col] = clean(table[i][idx] ?? '');
    });

    const person = cells.person ?? '';
    const hasData = columns.some((col) => col !== 'person' && cells[col]);
    if (!hasData) continue; // placeholder rows with only a person, or blank lines

    const problems: string[] = [];
    if (!person) problems.push('person is missing');
    const startDate = cells.from ?? '';
    if (!isValidDate(startDate)) problems.push(`"From" must be a YYYY-MM-DD date (got "${startDate}")`);
    const toRaw = cells.to ?? '';
    const endDate = PRESENT.test(toRaw) ? null : toRaw;
    if (endDate !== null && !isValidDate(endDate)) problems.push(`"To" must be a YYYY-MM-DD date or "Present" (got "${toRaw}")`);
    if (isValidDate(startDate) && endDate && isValidDate(endDate) && endDate < startDate) {
      problems.push(`"To" (${endDate}) is before "From" (${startDate})`);
    }
    if (!cells.city) problems.push('city is missing');
    if (!cells.country) problems.push('country is missing');

    if (problems.length > 0) {
      errors.push({ row: rowNumber, message: problems.join('; ') });
      continue;
    }

    rows.push({
      row: rowNumber,
      person,
      residence: {
        startDate,
        endDate,
        unit: cells.unit || null,
        streetNumber: cells.streetNumber || null,
        street: cells.street || null,
        city: cells.city!,
        region: cells.region || null,
        postalCode: cells.postalCode || null,
        country: cells.country!,
      },
    });
  }

  return { rows, errors };
}

export interface ResidenceSheetRow {
  person: string;
  from: string;
  to: string;
  unit: string;
  streetNumber: string;
  street: string;
  city: string;
  region: string;
  postalCode: string;
  country: string;
}

export const RESIDENCE_SHEET_HEADER = [
  'Person', 'FROM (YYYY-MM-DD)', 'TO (YYYY-MM-DD)', 'Unit/Apartment #', 'House/Street #',
  'Street address/Name', 'City/Town', 'Province/State', 'Postal Code/Zip Code', 'Country',
];

/**
 * Rows back to sheet text (tab-separated, header first), quoting cells with tabs, quotes or newlines.
 * Lets edited or AI-read rows go through exactly the same validation as a pasted sheet.
 */
export function residenceRowsToSheet(rows: ResidenceSheetRow[]): string {
  const cell = (v: string) => (/[\t\n"]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const lines = rows.map((r) =>
    [r.person, r.from, r.to, r.unit, r.streetNumber, r.street, r.city, r.region, r.postalCode, r.country].map((v) => cell(v ?? '')).join('\t')
  );
  return [RESIDENCE_SHEET_HEADER.join('\t'), ...lines].join('\n');
}

/** Parsed rows as editable sheet rows ("Present" for current addresses). */
export function toSheetRows(rows: ParsedResidenceRow[]): ResidenceSheetRow[] {
  return rows.map((r) => ({
    person: r.person,
    from: r.residence.startDate,
    to: r.residence.endDate ?? 'Present',
    unit: r.residence.unit ?? '',
    streetNumber: r.residence.streetNumber ?? '',
    street: r.residence.street ?? '',
    city: r.residence.city,
    region: r.residence.region ?? '',
    postalCode: r.residence.postalCode ?? '',
    country: r.residence.country,
  }));
}

/** Residences active on a YYYY-MM-DD date. Moves can overlap by a few days, so several may be active. */
export function activeResidencesOn<T extends Pick<Residence, 'startDate' | 'endDate'>>(residences: T[], date: string): T[] {
  return residences.filter((r) => r.startDate <= date && (r.endDate === null || date <= r.endDate));
}

/** Pairs of residences whose periods overlap by more than `toleranceDays` (short overlaps are normal when moving). */
export function findResidenceOverlaps<T extends Pick<Residence, 'startDate' | 'endDate'>>(
  residences: T[],
  toleranceDays = 31
): Array<[T, T]> {
  const sorted = [...residences].sort((a, b) => a.startDate.localeCompare(b.startDate));
  const overlaps: Array<[T, T]> = [];
  for (let i = 0; i < sorted.length; i++) {
    for (let j = i + 1; j < sorted.length; j++) {
      const a = sorted[i];
      const b = sorted[j];
      const aEnd = a.endDate ?? '9999-12-31';
      if (b.startDate > aEnd) continue;
      const overlapEnd = b.endDate === null || b.endDate > aEnd ? aEnd : b.endDate;
      if (dayDiff(b.startDate, overlapEnd) > toleranceDays) overlaps.push([a, b]);
    }
  }
  return overlaps;
}

function parseTsv(text: string): string[][] {
  const table: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  const input = text.replace(/\r\n?/g, '\n');

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];
    if (inQuotes) {
      if (ch === '"' && input[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') inQuotes = false;
      else cell += ch;
    } else if (ch === '"' && cell === '') {
      inQuotes = true;
    } else if (ch === '\t') {
      row.push(cell); cell = '';
    } else if (ch === '\n') {
      row.push(cell); table.push(row); row = []; cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell !== '' || row.length > 0) { row.push(cell); table.push(row); }
  return table.filter((r) => r.some((c) => c.trim() !== ''));
}

function detectHeader(cells: string[]): Column[] | null {
  const normalized = cells.map((c) => clean(c).toLowerCase());
  if (!normalized.some((c) => /person|city|country/.test(c))) return null;
  const used = new Set<Column>();
  const columns = normalized.map((header) => {
    const match = HEADER_PATTERNS.find(([col, re]) => !used.has(col) && re.test(header));
    if (!match) return null;
    used.add(match[0]);
    return match[0];
  });
  // Unknown header cells map to nothing; keep positions aligned with a sentinel that is never read.
  return columns.map((c) => c ?? ('ignored' as Column));
}

function clean(value: string): string {
  return value.split('\n').map((s) => s.trim()).filter(Boolean).join(', ').replace(/\s+/g, ' ').trim();
}

function isValidDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

function dayDiff(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}
